import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Check, Clock, Star, Sparkles, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const RETREAT_BANNER_IMG = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897056/ChatGPT_Image_27_may_2026_17_46_18_n4puov.png';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso);
  const now = new Date();
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

/** Status → visual mapping (label + tone + icon) */
const STATUS_META = {
  waitlist: {
    label: 'En lista de espera',
    note: 'Tu reserva está guardada. Confirma tu plaza con la paga y señal.',
    Icon: Clock,
    tone: 'amber',
  },
  deposit_paid: {
    label: 'Plaza reservada',
    note: 'Paga y señal recibida. Te esperamos en El Olivar.',
    Icon: Check,
    tone: 'emerald',
  },
  partially_paid: {
    label: 'Plaza reservada · pago parcial',
    note: 'Quedan plazos por completar antes del retiro.',
    Icon: Check,
    tone: 'emerald',
  },
  confirmed: {
    label: 'Plaza confirmada',
    note: '¡Todo listo! Revisa tus datos antes de partir.',
    Icon: Check,
    tone: 'emerald',
  },
  cancelled: {
    label: 'Plaza cancelada',
    note: 'Si fue un error, contáctanos para recuperarla.',
    Icon: AlertCircle,
    tone: 'gray',
  },
};

const TONE_CLS = {
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  gray: 'bg-gray-50 text-gray-600 border-gray-200',
};

/**
 * Card del retiro en Mi Camino:
 * - Banner imagen completa arriba (sin texto sobrepuesto que tape la portada original)
 * - Panel inferior con: estado actual, fechas, lugar, días que faltan, CTA acceso espacio privado
 * - Si no está inscrita aún: panel inferior muestra CTA "Reservar mi plaza" hacia la landing pública
 */
const RetreatCard = () => {
  const { user } = useAuth();
  const [retreat, setRetreat] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [specialPricing, setSpecialPricing] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/retreats')
      .then(async (r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        const now = Date.now();
        const chosen = list.find(x => x.show_mi_camino_card && x.date_start &&
          new Date(x.date_start).getTime() > now);
        setRetreat(chosen || null);
        if (chosen && user) {
          try {
            const me = await api.get(`/retreats/${chosen.slug}/me`);
            if (!cancelled) setEnrollment(me.data?.enrollment || null);
          } catch { /* not enrolled */ }
          try {
            const sp = await api.get(`/retreats/${chosen.slug}/special-pricing/me`);
            if (!cancelled && sp.data?.eligible) setSpecialPricing(sp.data);
          } catch { /* not eligible */ }
        }
      })
      .catch(() => { /* none */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user]);

  // If the user lands on Mi Camino with ?retreat=<slug> (e.g. just after signup),
  // scroll the card into view and highlight it briefly.
  const [highlight, setHighlight] = useState(false);
  useEffect(() => {
    if (!loaded || !retreat) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('retreat') === retreat.slug) {
      const el = document.querySelector('[data-testid="retreat-card-mi-camino"]');
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlight(true);
          setTimeout(() => setHighlight(false), 2500);
        }, 200);
      }
    }
  }, [loaded, retreat]);

  if (!loaded || !retreat) return null;

  const isEnrolled = !!enrollment;
  const statusMeta = isEnrolled ? STATUS_META[enrollment.status] || STATUS_META.waitlist : null;
  const days = daysUntil(retreat.date_start);

  return (
    <div
      className={`block mb-8 rounded-3xl overflow-hidden border bg-white transition-all duration-700 ${
        highlight
          ? 'border-[#D4A84B] shadow-2xl shadow-[#D4A84B]/40 ring-4 ring-[#D4A84B]/30 scale-[1.01]'
          : 'border-[#D4A84B]/30 shadow-sm hover:shadow-lg hover:shadow-[#D4A84B]/10'
      }`}
      data-testid="retreat-card-mi-camino"
    >
      {/* IMAGE BANNER — always links to public landing so anyone can discover the retreat */}
      <Link to={`/retiros/${retreat.slug}`} className="block group" data-testid="retreat-banner-link">
        <div className="relative w-full overflow-hidden bg-[#0f1729]">
          <img
            src={RETREAT_BANNER_IMG}
            alt="REINICIA 2026 · Retiro presencial en el Monasterio del Olivar"
            className="block w-full h-auto group-hover:scale-[1.01] transition-transform duration-700"
            data-testid="retreat-banner-img"
          />
        </div>
      </Link>

      {/* STATUS + CTA STRIP */}
      <div className="p-6 sm:p-8 bg-gradient-to-b from-white to-[#FDFBF5]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

          {/* LEFT: status + meta */}
          <div className="flex-1 min-w-0">
            {isEnrolled ? (
              <>
                <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-1 rounded-full border ${TONE_CLS[statusMeta.tone]} mb-3`}
                     data-testid={`status-pill-${enrollment.status}`}>
                  <statusMeta.Icon className="w-3.5 h-3.5" />
                  {statusMeta.label}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-3 max-w-xl">
                  {statusMeta.note}
                </p>
              </>
            ) : (
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-1 rounded-full border bg-[#FDFBF5] text-[#8a7428] border-[#D4A84B]/40 mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                Reservas abiertas
              </div>
            )}

            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs sm:text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#D4A84B]" />
                {formatDate(retreat.date_start)} – {formatDate(retreat.date_end)} {new Date(retreat.date_end).getFullYear()}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#D4A84B]" />
                {retreat.location_name}, {retreat.location_city}
              </span>
              {days != null && days > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 text-[#D4A84B] fill-current" />
                  faltan <strong className="text-[#8a7428] font-medium">{days} días</strong>
                </span>
              )}
            </div>

            {specialPricing && (
              <div className="mt-4 max-w-xl" data-testid="retreat-special-pricing-wrap">
                <div
                  className="inline-flex flex-wrap items-center gap-2 sm:gap-3 px-3 py-2 rounded-2xl bg-gradient-to-r from-[#FDFBF5] via-white to-[#FDFBF5] border border-[#D4A84B]/40 shadow-[0_1px_8px_rgba(212,168,75,0.12)]"
                  data-testid="retreat-special-pricing-banner"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#D4A84B] flex-shrink-0" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#8a7428] font-medium">
                    {specialPricing.title || 'Tarifa alumnado actual'}
                  </span>
                  <span className="inline-flex items-baseline gap-1.5 text-sm">
                    <span className="line-through text-gray-400 text-xs">
                      {Number(specialPricing.regular_price_double).toFixed(2).replace('.', ',')} €
                    </span>
                    <strong className="font-heading text-base text-[#5e4f1a]">
                      {Number(specialPricing.price_double).toFixed(2).replace('.', ',')} €
                    </strong>
                    <span className="text-[10px] text-gray-500">· hab. doble</span>
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 italic mt-1.5 leading-relaxed pl-1">
                  Beneficio disponible para alumnas en activo de la 3ª edición de Facilitadoras en Sostén Energético o 1ª edición de LÍMITES.
                </p>
              </div>
            )}

            {isEnrolled && enrollment.intention_text && (
              <p className="text-xs text-gray-500 italic mt-3 line-clamp-2 max-w-xl" style={{ fontFamily: 'Georgia, serif' }}>
                «{enrollment.intention_text}»
              </p>
            )}
          </div>

          {/* RIGHT: CTAs */}
          <div className="flex-shrink-0 flex flex-col gap-2 sm:items-end">
            {isEnrolled ? (
              <Link
                to={`/mi-retiro/${retreat.slug}`}
                className="inline-flex items-center gap-2 bg-[#2d3748] hover:bg-black text-white px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-md shadow-[#D4A84B]/10"
                data-testid="retreat-cta-button"
              >
                Entrar a mi espacio del retiro
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                {/* Botón 1 (azul iniciático) — más información en la landing */}
                <Link
                  to={`/retiros/${retreat.slug}`}
                  className="inline-flex items-center gap-2 bg-[#2d3748] hover:bg-[#1a1f2e] text-white px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors"
                  data-testid="retreat-info-button"
                >
                  Conocer el retiro
                  <ArrowRight className="w-4 h-4" />
                </Link>
                {/* Botón 2 (dorado) — reservar plaza directamente */}
                <Link
                  to={`/retiros/${retreat.slug}?signup=1`}
                  className="inline-flex items-center gap-2 bg-[#D4A84B] hover:bg-[#b89220] text-[#1a140a] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-md shadow-[#D4A84B]/30"
                  data-testid="retreat-cta-button"
                >
                  Reservar mi plaza gratuitamente
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetreatCard;
